import { call, put, select } from 'redux-saga/effects';
import { requestGetUser, requestGetManagers, requestGetAllUsers, requestCreateUser, requestUpdateUser, requestUploadUserAvatar, requestRemoveUserAvatar, requestDeleteUser, requestRestoreUser, requestActivateUser, requestDeactivateUser, requestGetUserDeactivationImpact, requestUpdateUserPassword, requestAddUserAccounts, requestRemoveUserAccounts, requestUpdateUserRole, requestUpdateUserEmail, requestUserAccessFlags, requestSetUserApiAccess, requestUserAssignments, requestUpdateUserAccountRole, requestUpdatePinnedAccounts, requestToggleStarredItem, requestGetMentionableServiceUsers } from '../requests/users';
import { setCurrentUser, setPinnedAccounts } from '../../slices/auth';
import { serviceUsersReceived } from '../../slices/users';
import { showAlert } from '../../slices/alerts';
import { safeErrorMessage } from '../../../utils/errorUtils';

export function* handleFetchMentionableServiceUsers() {
    try {
        const response = yield call(requestGetMentionableServiceUsers);
        yield put(serviceUsersReceived(response.data || []));
    } catch (error) {
        yield put(serviceUsersReceived([]));
    }
}

export function* handleGetUser(action) {
    try {
        const { userId, callback } = action.payload;
        const response = yield call(requestGetUser, userId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleGetManagers(action) {
    try {
        const { callback } = action.payload;
        const response = yield call(requestGetManagers);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleGetAllUsers(action) {
    try {
        const { callback } = action.payload;
        const response = yield call(requestGetAllUsers);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleCreateUser(action) {
    try {
        const { userData, callback } = action.payload;
        const response = yield call(requestCreateUser, userData);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'User created successfully' }));
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleUpdateUser(action) {
    try {
        const { userId, userData, callback, silent = false } = action.payload;
        const response = yield call(requestUpdateUser, userId, userData);
        if (callback) {
            callback(response.data);
        }
        if (!silent) {
            yield put(showAlert({ severity: 'success', message: 'User updated successfully' }));
        }
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleUploadUserAvatar(action) {
    try {
        const { formData, callback } = action.payload;
        const response = yield call(requestUploadUserAvatar, formData);
        const data = response.data || {};
        const auth = yield select((state) => state.auth);
        if (auth?.currentUser && data.user && String(data.user._id) === String(auth.userId)) {
            yield put(setCurrentUser({
                user: { ...auth.currentUser, ...data.user },
                id: auth.userId,
                role: auth.systemRole,
                permissions: auth.permissions,
            }));
        }
        if (callback) {
            callback(data);
        }
        yield put(showAlert({ severity: 'success', message: 'Avatar updated successfully' }));
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: error?.response?.data?.msg || error?.message || 'Failed to upload avatar' }));
    }
}

export function* handleRemoveUserAvatar(action) {
    try {
        const { userId, callback } = action.payload;
        const response = yield call(requestRemoveUserAvatar, userId);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'Avatar removed successfully' }));
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: error?.response?.data?.msg || error?.message || 'Failed to remove avatar' }));
    }
}

export function* handleDeleteUser(action) {
    try {
        const { userId, callback } = action.payload;
        const response = yield call(requestDeleteUser, userId);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'User deleted successfully' }));
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleRestoreUser(action) {
    try {
        const { userId, callback } = action.payload;
        const response = yield call(requestRestoreUser, userId);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'User restored successfully' }));
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleActivateUser(action) {
    try {
        const { userId, callback } = action.payload;
        const response = yield call(requestActivateUser, userId);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'User activated successfully' }));
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleDeactivateUser(action) {
    try {
        const { userId, callback } = action.payload;
        const response = yield call(requestDeactivateUser, userId);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'User deactivated successfully' }));
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleGetDeactivationImpact(action) {
    try {
        const { userId, callback } = action.payload;
        const response = yield call(requestGetUserDeactivationImpact, userId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}

export function* handleUpdateUserPassword(action) {
    try {
        const { userId, password, currentPassword, mustChangePassword, callback } = action.payload;
        const response = yield call(requestUpdateUserPassword, userId, password, currentPassword, mustChangePassword);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'Password updated successfully' }));
    } catch (error) {
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error, 'Password update failed') }));
    }
}

export function* handleAddUserAccounts(action) {
    try {
        const { userId, accounts, callback } = action.payload;
        const response = yield call(requestAddUserAccounts, userId, accounts);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'Account(s) added successfully' }));
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleRemoveUserAccounts(action) {
    try {
        const { userId, accounts, callback } = action.payload;
        const response = yield call(requestRemoveUserAccounts, userId, accounts);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'Account(s) removed successfully' }));
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleUpdateUserAccountRole(action) {
    try {
        const { userId, accountId, newRole, callback } = action.payload;
        const response = yield call(requestUpdateUserAccountRole, userId, accountId, newRole);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'Account role updated successfully. New role: ' + newRole }));
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleUpdateUserRole(action) {
    try {
        const { userId, roleId, callback } = action.payload;
        const response = yield call(requestUpdateUserRole, userId, roleId);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'Role updated successfully' }));
    } catch (error) {
        const { callback } = action.payload;
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        // The level guards reject with a 403 the caller is expected to show in place, so the reason
        // travels back with the failure rather than only flashing past in the alert.
        if (callback) callback(null, { status: error?.response?.status, message });
    }
}

export function* handleUpdateUserEmail(action) {
    try {
        const { userId, email, callback } = action.payload;
        const response = yield call(requestUpdateUserEmail, userId, email);
        if (callback) {
            callback(response.data);
        }
        yield put(showAlert({ severity: 'success', message: 'Email updated successfully' }));
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleGetUserAccessFlags(action) {
    try {
        const { params, callback } = action.payload;
        const response = yield call(requestUserAccessFlags, params);
        if (callback) callback(response.data);
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleSetUserApiAccess(action) {
    try {
        const { userId, apiAccess, callback } = action.payload;
        const response = yield call(requestSetUserApiAccess, userId, apiAccess);
        if (callback) callback(response.data);
    } catch (error) {
        const { callback } = action.payload;
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleUserAssignments(action) {
    const { callback } = action.payload;
    try {
        const { userId } = action.payload;
        const response = yield call(requestUserAssignments, userId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleToggleStarredItem(action) {
    const { callback } = action.payload;
    try {
        const { objectId } = action.payload;
        const response = yield call(requestToggleStarredItem, objectId);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        if (callback) {
            callback(null);
        }
        yield put(showAlert({ severity: 'error', message: error?.response?.data?.msg || error?.message || 'Failed to update your starred items' }));
    }
}

export function* handleUpdatePinnedAccounts(action) {
    try {
        const { accountId } = action.payload;
        const response = yield call(requestUpdatePinnedAccounts, accountId);
        const { data } = response;;
        const { msg, pinnedAccounts } = data;
        if (msg) {
            yield put(showAlert({ severity: 'success', message: msg }));
        }
        if (pinnedAccounts) {
            yield put(setPinnedAccounts({ pinnedAccounts }));
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: error?.response?.data?.msg || error?.message || 'Failed to update pinned accounts' }));
    }
}